'use client';

import { useTranslations } from 'next-intl';
import React, { useMemo, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import { useOrientation, useWindowSize } from 'react-use';

import { TrailerGate } from '@cineamo/api/models/movie-trailer/TrailerPlayback.types';

import { resolveTrailerPlayback } from '@cineamo/api/helpers/trailer-playback-helper';

import { cn } from '@cineamo/ui/src/helpers/cn';
import { useIsMobile } from '@cineamo/ui/src/helpers/screen-type';
import { setIframeTitle } from '@cineamo/ui/src/helpers/set-iframe-title';

import { IconPlayButton56 } from '@cineamo/ui/src/icons/ic-playButton';
import { BackdropAndPosterCompositeImage } from '@cineamo/ui/src/image/BackdropAndPosterCompositeImage';
import ImageWithTransition from '@cineamo/ui/src/image/ImageWithTransition';
import FullScreenModal from '@cineamo/ui/src/modals/FullScreenModal';

import { useLegalClock } from './hooks/useLegalClock';
import TrailerUnavailableNotice from './movie/TrailerUnavailableNotice';
import { useAnalytics } from './providers/analyticsProvider';

type AllowedPropertyValues = string | number | boolean | null;

type TrailerAndPosterCompositeImageProps = {
    title?: string;
    backdropImage?: string;
    backdropBlurDataImage?: string;
    posterImage?: string;
    posterBlurDataImage?: string;
    trailerUrl?: string;
    /**
     * Age-rating gate for `trailerUrl` (CIN-14724). When it blocks embedding, the play button still
     * opens the modal — it just shows the reason and a YouTube link instead of the player. Keeping the
     * button live is deliberate: a dead control tells the viewer nothing.
     *
     * Omitted means ungated, which is what an operator back-office preview wants.
     */
    gate?: TrailerGate;
    showPoster?: boolean;
    hideTrailer?: boolean;
    className?: string;
    /** Renders a bare 72x108 poster (backdrop dropped) with the trailer button on top, for compact headers. */
    compact?: boolean;
    /** Passed through to `BackdropAndPosterCompositeImage` to enable responsive image loading. */
    sizes?: string;
    /** Eagerly load the images (LCP). Defaults to `true`; pass `false` for an instance that is hidden at some breakpoints. */
    priority?: boolean;
    /** Overrides `priority` for the poster overlay of the non-compact layout. */
    posterPriority?: boolean;
    trackingData?: Record<string, AllowedPropertyValues>;
    onTrailerPlay?: () => void;
    /**
     * Fired at most once per play session — when the viewer reaches the completion
     * threshold (85% played) or the video ends, whichever comes first.
     */
    onTrailerFinished?: () => void;
};

const TRAILER_FINISHED_THRESHOLD = 0.85;

export function TrailerAndPosterCompositeImage({
    title,
    backdropImage,
    backdropBlurDataImage,
    posterImage,
    posterBlurDataImage,
    trailerUrl,
    gate,
    showPoster,
    hideTrailer,
    className,
    compact,
    sizes,
    priority = true,
    posterPriority,
    trackingData,
    onTrailerPlay,
    onTrailerFinished
}: TrailerAndPosterCompositeImageProps) {
    const { track } = useAnalytics();
    const t = useTranslations();

    const isMobile = useIsMobile('lg');
    const orientation = useOrientation();
    const { width, height } = useWindowSize();

    const [trailerModalIsOpen, setTrailerModalIsOpen] = useState<boolean>(false);
    const headerVideoPlayerRef = useRef(null);
    const playerContainerRef = useRef<HTMLDivElement>(null);
    // Guards onTrailerFinished so it fires at most once per play session.
    // react-player's onProgress ticks ~once a second, so without this flag the
    // callback would fire on every tick past the threshold.
    const finishedFiredRef = useRef(false);

    const fireFinishedOnce = () => {
        if (!finishedFiredRef.current) {
            finishedFiredRef.current = true;
            onTrailerFinished?.();
        }
    };

    const playerSize = useMemo(() => {
        const ratio = 540 / 960;
        const playerHeightOnMobile = width * ratio + 100;
        return {
            width: isMobile ? width : 960,
            height: isMobile ? (orientation.type === 'landscape-primary' ? height : playerHeightOnMobile) : 540
        };
    }, [orientation.type, isMobile, width, height]);

    const showTrailerButton = hideTrailer !== true && trailerUrl && !trailerModalIsOpen;

    const legalClock = useLegalClock(gate);

    // Blocked trailers never reach ReactPlayer: an off-screen iframe that is playing is still
    // playback, and the JMStV obligation is that minors do not PERCEIVE the content.
    //
    // The clock is read here rather than only in the notice, and on mount rather than on click, so
    // that it has arrived by the time the modal opens — otherwise the first frame after a click is
    // always the "we cannot tell yet" notice.
    const isBlocked = gate ? !resolveTrailerPlayback(gate.rating, legalClock, gate.countryCode).embeddable : false;

    const trailerButton = showTrailerButton && (
        <div className="flex flex-auto items-center justify-center">
            <IconPlayButton56
                className="pointer-events-auto clickable-element text-black"
                foregroundcolor="white"
                role="button"
                aria-label="Play Trailer Button"
                onClick={(event) => {
                    event.stopPropagation();
                    track('Movie Trailer Played', trackingData);
                    finishedFiredRef.current = false;
                    onTrailerPlay?.();
                    setTrailerModalIsOpen(true);
                }}
            />
        </div>
    );

    return (
        <div
            className={cn({
                'pointer-events-auto': trailerModalIsOpen
            })}
            onClick={() => {
                if (trailerModalIsOpen) setTrailerModalIsOpen(false);
            }}>
            {trailerModalIsOpen && trailerUrl && (
                <FullScreenModal
                    modalIsOpen={trailerModalIsOpen}
                    setModalIsOpen={setTrailerModalIsOpen}
                    onClose={null}>
                    {isBlocked && gate ? (
                        <div
                            className="flex items-center justify-center"
                            style={{ width: playerSize.width, height: playerSize.height }}>
                            <TrailerUnavailableNotice gate={gate} />
                        </div>
                    ) : (
                        <div ref={playerContainerRef}>
                            <ReactPlayer
                                url={trailerUrl}
                                onReady={() =>
                                    setIframeTitle(
                                        playerContainerRef.current,
                                        title ? t('common.trailer-title', { title }) : t('common.trailer')
                                    )
                                }
                                playing={trailerModalIsOpen}
                                onEnded={() => {
                                    fireFinishedOnce();
                                    setTrailerModalIsOpen(false);
                                }}
                                onProgress={({ played }) => {
                                    if (played >= TRAILER_FINISHED_THRESHOLD) {
                                        fireFinishedOnce();
                                    }
                                }}
                                controls={true}
                                ref={headerVideoPlayerRef}
                                width={playerSize.width}
                                height={playerSize.height}
                            />
                        </div>
                    )}
                </FullScreenModal>
            )}
            {compact ? (
                <div
                    className={cn(
                        'relative w-[72px] h-[108px] shrink-0 rounded-4 overflow-hidden bg-black',
                        className
                    )}>
                    {(posterImage ?? backdropImage) && (
                        <ImageWithTransition
                            className="w-full h-full object-cover"
                            alt={title ? `${title} Poster` : 'event-poster-image'}
                            src={(posterImage ?? backdropImage) as string}
                            placeholder={posterBlurDataImage ? 'blur' : 'empty'}
                            blurDataURL={posterBlurDataImage}
                            width={300}
                            height={450}
                            sizes={sizes}
                            priority={priority}
                        />
                    )}
                    <div className="absolute inset-0 flex items-center justify-center">{trailerButton}</div>
                </div>
            ) : (
                <BackdropAndPosterCompositeImage
                    title={title}
                    backdropImage={backdropImage}
                    backdropBlurDataImage={backdropBlurDataImage}
                    posterImage={posterImage}
                    posterBlurDataImage={posterBlurDataImage}
                    showPoster={showPoster}
                    sizes={sizes}
                    posterPriority={posterPriority ?? priority}
                    backdropPriority={priority}
                    className={cn('h-210 lg:h-240 lg:aspect-[16/9]', className)}>
                    {trailerButton}
                </BackdropAndPosterCompositeImage>
            )}
        </div>
    );
}
